import React, { useState } from 'react';
import { Scissors, Save, Check, Plus, Trash2, ChevronRight, ChevronLeft } from 'lucide-react';
import { MarkupType, WalkInPPQuote, SelectedPPVendor } from '../types';
import { PP_VENDORS } from '../data/masterData';
import { calculateMarkup, formatCurrency, generateQuoteNumber } from '../utils/helpers';

interface WalkInPPProps { onSave: (q: WalkInPPQuote) => void; }

// Unique vendors
const VENDOR_NAMES = PP_VENDORS
  .filter(v => v.status === 'Active')
  .map(v => v.vendor)
  .filter((v, i, a) => a.indexOf(v) === i);

export default function WalkInPP({ onSave }: WalkInPPProps) {
  const [step, setStep] = useState(1); // 1=Customer, 2=Vendor, 3=Deliverables, 4=Markup+Save
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedItems, setSelectedItems] = useState<SelectedPPVendor[]>([]);
  const [markupType, setMarkupType] = useState<MarkupType>(45);
  const [saved, setSaved] = useState(false);

  const totalCost = selectedItems.reduce((s, v) => s + v.rate, 0);
  const { markupAmount, quotedAmount } = calculateMarkup(totalCost, markupType);

  // Deliverables for selected vendor (deduplicated)
  const vendorDeliverables = PP_VENDORS
    .filter(v => v.status === 'Active' && v.vendor === selectedVendor)
    .filter((v, i, a) => a.findIndex(x => x.deliverable === v.deliverable) === i);

  const toggleItem = (deliverable: string, rate: number) => {
    setSelectedItems(prev => {
      const exists = prev.some(i => i.vendor === selectedVendor && i.deliverable === deliverable);
      if (exists) return prev.filter(i => !(i.vendor === selectedVendor && i.deliverable === deliverable));
      return [...prev, { vendor: selectedVendor, deliverable, rate }];
    });
  };

  const removeItem = (vendor: string, deliverable: string) =>
    setSelectedItems(prev => prev.filter(i => !(i.vendor === vendor && i.deliverable === deliverable)));

  const handleSave = () => {
    if (!customerName || selectedItems.length === 0) return;
    const q: WalkInPPQuote = {
      id: `wipp_${Date.now()}`, quoteNumber: generateQuoteNumber(),
      customerName, customerPhone, createdAt: new Date().toISOString(),
      status: 'draft', ppVendors: selectedItems, markupType,
      totalCost, markupAmount, quotedAmount,
    };
    onSave(q);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (saved) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 }}>
        <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Check size={26} color="white" />
        </div>
        <h2>Quote Saved!</h2>
        <p style={{ color: 'var(--text-secondary)' }}>Walk-in PP quote created successfully.</p>
      </div>
    );
  }

  // Step labels
  const STEPS = ['Customer', 'Vendor', 'Deliverables', 'Review & Save'];

  return (
    <div style={{ padding: 28, maxWidth: 720 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Scissors size={20} color="var(--gold)" />
        <div>
          <h1>Walk-in Post Production</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 2 }}>Customer brings footage — editing only quote</p>
        </div>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28 }}>
        {STEPS.map((s, i) => (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
              <div className={`step-dot ${i + 1 === step ? 'step-dot-active' : i + 1 < step ? 'step-dot-done' : 'step-dot-inactive'}`}>
                {i + 1 < step ? <Check size={12} /> : i + 1}
              </div>
              <span style={{ fontSize: 11, fontWeight: i + 1 === step ? 600 : 400, color: i + 1 === step ? 'var(--gold)' : i + 1 < step ? 'var(--teal)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                {s}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 1, background: i + 1 < step ? 'var(--teal)' : 'var(--border)', margin: '0 8px', marginBottom: 20, minWidth: 16 }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1 — Customer */}
      {step === 1 && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Customer Details</h3>
          <div className="form-row">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Name *</label>
              <input className="input" placeholder="Customer name" value={customerName} onChange={e => setCustomerName(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Phone</label>
              <input className="input" placeholder="Phone number" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button className="btn btn-primary" onClick={() => setStep(2)} disabled={!customerName.trim()}>
              Continue <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Step 2 — Select Vendor */}
      {step === 2 && (
        <div className="card">
          <h3 style={{ marginBottom: 6 }}>Select Vendor</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>Choose the post production vendor for this job.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {VENDOR_NAMES.map(v => (
              <button key={v} onClick={() => setSelectedVendor(v)}
                className={`option-card ${selectedVendor === v ? 'selected' : ''}`}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: selectedVendor === v ? 'var(--gold-bg)' : 'var(--bg-hover)', border: `1px solid ${selectedVendor === v ? 'var(--gold)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: selectedVendor === v ? 'var(--gold)' : 'var(--text-muted)' }}>
                  {v[0]}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: selectedVendor === v ? 'var(--gold)' : 'var(--text-primary)' }}>{v}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {PP_VENDORS.filter(p => p.vendor === v && p.status === 'Active').filter((p, i, a) => a.findIndex(x => x.deliverable === p.deliverable) === i).length} deliverables available
                  </div>
                </div>
                {selectedVendor === v && <Check size={16} style={{ marginLeft: 'auto', color: 'var(--gold)' }} />}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
            <button className="btn btn-secondary" onClick={() => setStep(1)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary" onClick={() => setStep(3)} disabled={!selectedVendor}>
              Continue <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Step 3 — Select Deliverables */}
      {step === 3 && (
        <div>
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 6 }}>{selectedVendor} — Deliverables</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>Select the deliverables for this job. Tap to add/remove.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {vendorDeliverables.map((d, i) => {
                const rates = Object.values(d.rates).filter(r => r !== undefined) as number[];
                if (!rates.length) return null;
                const minRate = Math.min(...rates);
                const maxRate = Math.max(...rates);
                const rateStr = minRate === maxRate ? formatCurrency(minRate) : `${formatCurrency(minRate)} – ${formatCurrency(maxRate)}`;
                const isSelected = selectedItems.some(s => s.vendor === selectedVendor && s.deliverable === d.deliverable);
                return (
                  <button key={i} onClick={() => toggleItem(d.deliverable, minRate)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                      borderRadius: 'var(--radius-sm)', cursor: 'pointer', textAlign: 'left',
                      background: isSelected ? 'var(--teal-bg)' : '#fff',
                      border: `1.5px solid ${isSelected ? 'var(--teal)' : 'var(--border)'}`,
                      fontFamily: 'inherit', transition: 'all 0.12s', width: '100%',
                    }}>
                    <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${isSelected ? 'var(--teal)' : 'var(--border-light)'}`, background: isSelected ? 'var(--teal)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {isSelected && <Check size={12} color="white" />}
                    </div>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: isSelected ? 600 : 400, color: 'var(--text-primary)' }}>{d.deliverable}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: isSelected ? 'var(--teal)' : 'var(--text-secondary)' }}>{rateStr}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Add more from other vendor */}
          <button onClick={() => setStep(2)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px dashed var(--border-light)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, width: '100%', justifyContent: 'center', marginBottom: 14, fontFamily: 'inherit' }}>
            <Plus size={13} /> Add deliverables from another vendor
          </button>

          {selectedItems.length > 0 && (
            <div className="card" style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Selected ({selectedItems.length})</div>
              {selectedItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--teal-bg)', border: '1px solid #9AE6B4', borderRadius: 6, marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--teal)', minWidth: 70 }}>{item.vendor}</span>
                  <span style={{ flex: 1, fontSize: 13 }}>{item.deliverable}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal)' }}>{formatCurrency(item.rate)}</span>
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={() => removeItem(item.vendor, item.deliverable)}>
                    <Trash2 size={12} color="var(--red)" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button className="btn btn-secondary" onClick={() => setStep(2)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary" onClick={() => setStep(4)} disabled={selectedItems.length === 0}>
              Continue <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Step 4 — Markup + Save */}
      {step === 4 && (
        <div>
          {/* Summary */}
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 12 }}>Order Summary</h3>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>CUSTOMER</div>
              <div style={{ fontWeight: 600 }}>{customerName}</div>
              {customerPhone && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{customerPhone}</div>}
            </div>
            <div className="divider" />
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>DELIVERABLES</div>
            {selectedItems.map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                <span>{item.vendor} — {item.deliverable}</span>
                <span style={{ fontWeight: 600 }}>{formatCurrency(item.rate)}</span>
              </div>
            ))}
          </div>

          {/* Markup */}
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 12 }}>Markup</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              {([35, 45, 55] as MarkupType[]).map(pct => (
                <button key={pct} onClick={() => setMarkupType(pct)}
                  style={{ flex: 1, padding: '10px 0', borderRadius: 'var(--radius-sm)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.12s', background: markupType === pct ? 'var(--gold)' : '#fff', color: markupType === pct ? '#fff' : 'var(--text-secondary)', border: `1.5px solid ${markupType === pct ? 'var(--gold)' : 'var(--border)'}` }}>
                  {pct}%
                </button>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)' }}>Base cost</span>
              <span>{formatCurrency(totalCost)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)' }}>Markup ({markupType}%)</span>
              <span style={{ color: 'var(--amber)' }}>{formatCurrency(markupAmount)}</span>
            </div>
            <div className="divider" />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>Quote</span>
              <span style={{ fontWeight: 700, fontSize: 22, color: 'var(--gold)' }}>{formatCurrency(quotedAmount)}</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button className="btn btn-secondary" onClick={() => setStep(3)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary btn-lg" onClick={handleSave}>
              <Save size={15} /> Save Quote
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
